/**
 * Modal Image Slider - 공통 모달 이미지 슬라이더 컴포넌트
 * women-style-grid와 styling-with-fila에서 공통으로 사용
 */
class ModalImageSlider {
  constructor(modalSlider, modalTrack) {
    this.modalSlider = modalSlider;
    this.modalTrack = modalTrack;
    this.modalIndex = 0;
    this.modalTotal = 0;
    this.isTransitioning = false;

    if (this.modalTrack) {
      this.initDragScroll();
      this.initScrollListener();
    }
  }

  // 이미지 설정 및 슬라이더 초기화
  setImages(images) {
    if (!this.modalTrack || !images || images.length === 0) return;

    this.modalTrack.innerHTML = '';
    this.modalTotal = images.length;

    // 이미지가 1개면 복제 슬라이드 불필요
    if (images.length === 1) {
      const slideEl = this.createSlide(images[0]);
      this.modalTrack.appendChild(slideEl);
      this.modalIndex = 0;
      this.modalTrack.scrollLeft = 0;
      this.updateDots();
      return;
    }

    // 마지막 슬라이드 복제 (맨 앞에 추가)
    const lastClone = this.createSlide(images[images.length - 1], true);
    this.modalTrack.appendChild(lastClone);

    // 원본 슬라이드들
    images.forEach((imgData) => {
      const slideEl = this.createSlide(imgData);
      this.modalTrack.appendChild(slideEl);
    });

    // 첫 번째 슬라이드 복제 (맨 뒤에 추가)
    const firstClone = this.createSlide(images[0], true);
    this.modalTrack.appendChild(firstClone);

    // 슬라이더 상태 초기화
    this.modalIndex = 0;
    if (this.modalSlider) {
      this.modalSlider.style.setProperty('--modal-slides', this.modalTotal);
      this.modalSlider.style.setProperty('--modal-slide-index', 0);
    }

    // 첫 번째 실제 슬라이드 위치로 이동 (복제 슬라이드 다음)
    this.modalTrack.style.scrollBehavior = 'auto';
    this.modalTrack.scrollLeft = this.modalTrack.clientWidth || 0;
    this.modalTrack.style.scrollBehavior = '';

    // 닷 생성
    this.createDots();
  }

  createSlide(imgData, isClone = false) {
    const slideEl = document.createElement('div');
    slideEl.className = 'women-styles-modal__slide' + (isClone ? ' women-styles-modal__slide--clone' : '');
    const newImg = document.createElement('img');
    newImg.src = imgData.src;
    newImg.alt = imgData.alt || 'Style image';
    newImg.className = 'women-styles-modal__image';
    newImg.draggable = false;
    slideEl.appendChild(newImg);
    return slideEl;
  }

  createDots() {
    if (!this.modalSlider) return;

    // 기존 닷 제거
    let dotsContainer = this.modalSlider.querySelector('.women-styles-modal__dots');
    if (dotsContainer) {
      dotsContainer.remove();
    }

    if (this.modalTotal <= 1) return;

    dotsContainer = document.createElement('div');
    dotsContainer.className = 'women-styles-modal__dots';
    for (let i = 0; i < this.modalTotal; i++) {
      const dot = document.createElement('span');
      dot.className = 'women-styles-modal__dot' + (i === 0 ? ' active' : '');
      dot.setAttribute('data-modal-dot-index', i);
      dot.addEventListener('click', () => this.goTo(i));
      dotsContainer.appendChild(dot);
    }
    this.modalSlider.appendChild(dotsContainer);
  }

  updateDots() {
    if (!this.modalSlider) return;
    const dots = this.modalSlider.querySelectorAll('.women-styles-modal__dot');
    let realIndex = this.modalIndex;
    if (this.modalIndex >= this.modalTotal) {
      realIndex = 0;
    } else if (this.modalIndex < 0) {
      realIndex = this.modalTotal - 1;
    }
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === realIndex);
    });
  }

  goTo(newIndex, instant = false) {
    if (this.isTransitioning) return;
    if (this.modalTotal <= 1) return;

    this.modalIndex = newIndex;

    if (this.modalTrack) {
      // 복제 슬라이드 포함한 실제 슬라이드 인덱스 (+1 offset for prepended clone)
      const slideIndex = this.modalIndex + 1;

      if (instant) {
        this.modalTrack.style.scrollBehavior = 'auto';
        this.modalTrack.scrollLeft = slideIndex * (this.modalTrack.clientWidth || 0);
        this.modalTrack.style.scrollBehavior = '';
      } else {
        this.isTransitioning = true;
        this.modalTrack.scrollTo({
          left: slideIndex * (this.modalTrack.clientWidth || 0),
          behavior: 'smooth'
        });

        // 애니메이션 완료 후 위치 조정
        setTimeout(() => {
          this.isTransitioning = false;

          // 복제 슬라이드에서 실제 슬라이드로 즉시 이동
          if (this.modalIndex >= this.modalTotal) {
            this.modalIndex = 0;
            this.modalTrack.style.scrollBehavior = 'auto';
            this.modalTrack.scrollLeft = 1 * (this.modalTrack.clientWidth || 0);
            this.modalTrack.style.scrollBehavior = '';
          } else if (this.modalIndex < 0) {
            this.modalIndex = this.modalTotal - 1;
            this.modalTrack.style.scrollBehavior = 'auto';
            this.modalTrack.scrollLeft = this.modalTotal * (this.modalTrack.clientWidth || 0);
            this.modalTrack.style.scrollBehavior = '';
          }
        }, 350);
      }
    }

    if (this.modalSlider) {
      this.modalSlider.style.setProperty('--modal-slide-index', this.modalIndex);
    }
    this.updateDots();
  }

  next() {
    this.goTo(this.modalIndex + 1);
  }

  prev() {
    this.goTo(this.modalIndex - 1);
  }

  initScrollListener() {
    let scrollTimeout;
    this.modalTrack.addEventListener('scroll', () => {
      if (this.isTransitioning) return;
      if (this.modalTotal <= 1) return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const slideWidth = this.modalTrack.clientWidth || 1;
        // 복제 슬라이드를 고려한 인덱스 계산 (-1 for prepended clone)
        const rawIndex = Math.round(this.modalTrack.scrollLeft / slideWidth) - 1;

        // 복제 슬라이드에서 실제 슬라이드로 즉시 이동
        if (rawIndex < 0) {
          this.modalIndex = this.modalTotal - 1;
          this.modalTrack.style.scrollBehavior = 'auto';
          this.modalTrack.scrollLeft = this.modalTotal * slideWidth;
          this.modalTrack.style.scrollBehavior = '';
        } else if (rawIndex >= this.modalTotal) {
          this.modalIndex = 0;
          this.modalTrack.style.scrollBehavior = 'auto';
          this.modalTrack.scrollLeft = slideWidth;
          this.modalTrack.style.scrollBehavior = '';
        } else {
          this.modalIndex = rawIndex;
        }

        if (this.modalSlider) {
          this.modalSlider.style.setProperty('--modal-slide-index', this.modalIndex);
        }
        this.updateDots();
      }, 50);
    });
  }

  initDragScroll() {
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasDragged = false;
    let velocity = 0;
    let lastX = 0;
    let lastTime = 0;

    this.modalTrack.addEventListener('mousedown', (e) => {
      isDown = true;
      hasDragged = false;
      this.modalTrack.classList.add('dragging');
      startX = e.pageX;
      lastX = e.pageX;
      scrollLeft = this.modalTrack.scrollLeft;
      lastTime = Date.now();
      velocity = 0;
      e.preventDefault();
    });

    this.modalTrack.addEventListener('mouseleave', () => {
      if (isDown && hasDragged) {
        this.snapToSlide(velocity, lastX - startX);
      }
      isDown = false;
      this.modalTrack.classList.remove('dragging');
    });

    this.modalTrack.addEventListener('mouseup', () => {
      if (hasDragged) {
        this.snapToSlide(velocity, lastX - startX);
      }
      isDown = false;
      this.modalTrack.classList.remove('dragging');
    });

    this.modalTrack.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();

      const x = e.pageX;
      const now = Date.now();
      const dt = now - lastTime;

      if (dt > 0) {
        velocity = (lastX - x) / dt;
      }

      lastX = x;
      lastTime = now;

      const walk = x - startX;
      if (Math.abs(walk) > 5) {
        hasDragged = true;
      }
      this.modalTrack.scrollLeft = scrollLeft - walk;
    });
  }

  snapToSlide(velocity, dragDistance) {
    if (this.modalTotal <= 1) return;

    const slideWidth = this.modalTrack.clientWidth || 1;
    const threshold = slideWidth * 0.15;

    let targetIndex = this.modalIndex;

    if (Math.abs(velocity) > 0.3) {
      if (velocity > 0) {
        targetIndex = this.modalIndex + 1;
      } else {
        targetIndex = this.modalIndex - 1;
      }
    } else if (Math.abs(dragDistance) > threshold) {
      if (dragDistance < 0) {
        targetIndex = this.modalIndex + 1;
      } else {
        targetIndex = this.modalIndex - 1;
      }
    }

    this.goTo(targetIndex);
  }

  reset() {
    this.modalIndex = 0;
    this.modalTotal = 0;
    this.isTransitioning = false;
    if (this.modalTrack) {
      this.modalTrack.innerHTML = '';
    }
    if (this.modalSlider) {
      const dotsContainer = this.modalSlider.querySelector('.women-styles-modal__dots');
      if (dotsContainer) {
        dotsContainer.remove();
      }
    }
  }
}

// 전역으로 노출
window.ModalImageSlider = ModalImageSlider;
